import { PositiveDecimal20x8StringSchema } from "./decimal-string";
import { z } from "zod";

// One armed market order per player per competition, placed at the start price when the competition goes live.

export const ArenaOpeningMoveSideSchema = z.enum(["LONG", "SHORT"]);
export type ArenaOpeningMoveSide = z.infer<typeof ArenaOpeningMoveSideSchema>;

export const ArenaOpeningMoveStatusSchema = z.enum(["ARMED", "FILLED", "FAILED", "CANCELLED"]);
export type ArenaOpeningMoveStatus = z.infer<typeof ArenaOpeningMoveStatusSchema>;

/** Body for `PUT v1/cede-arena/campaigns/:name/opening-move`; replaces any armed move. Size = margin × leverage / start price. */
export const SetArenaOpeningMoveRequestSchema = z.object({
  pairSymbol: z.string().min(1),
  side: ArenaOpeningMoveSideSchema,
  marginUsd: PositiveDecimal20x8StringSchema,
  leverage: z.number().int().min(1),
  takeProfitPrice: PositiveDecimal20x8StringSchema.nullish(),
  stopLossPrice: PositiveDecimal20x8StringSchema.nullish(),
});
export type SetArenaOpeningMoveRequest = z.infer<typeof SetArenaOpeningMoveRequestSchema>;

export const ArenaOpeningMoveDtoSchema = z.object({
  campaignName: z.string(),
  pairSymbol: z.string(),
  side: ArenaOpeningMoveSideSchema,
  marginUsd: z.string(),
  leverage: z.number().int(),
  takeProfitPrice: z.string().nullable(),
  stopLossPrice: z.string().nullable(),
  status: ArenaOpeningMoveStatusSchema,
  /** Set only when `status` is FAILED. */
  failureReason: z.string().nullable(),
  /** Set only when `status` is FILLED. */
  fillPrice: z.string().nullable(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
});
export type ArenaOpeningMoveDto = z.infer<typeof ArenaOpeningMoveDtoSchema>;

/** GET, PUT and DELETE `v1/cede-arena/campaigns/:name/opening-move` all answer with the caller's move; `null` = none. */
export const GetArenaOpeningMoveResponseSchema = z.object({
  openingMove: ArenaOpeningMoveDtoSchema.nullable(),
  /**
   * Whether the caller may arm or change a move right now: a format that supports it (scheduled
   * and Battle Royale competitions, private rooms; not matchmade duels or Spin & Go), the caller
   * has joined, and arming is still open (until 5 s before the start).
   */
  canArmOpeningMove: z.boolean(),
});
export type GetArenaOpeningMoveResponse = z.infer<typeof GetArenaOpeningMoveResponseSchema>;

/** Error codes the opening-move endpoints return in the RFC 9457 `type` field; the API's error codes carry the same strings. */
export const ARENA_OPENING_MOVE_ERROR_CODE = {
  NOT_OPTED_IN: "ARENA_OPENING_MOVE_NOT_OPTED_IN",
  ARMING_CLOSED: "ARENA_OPENING_MOVE_ARMING_CLOSED",
  PAIR_NOT_OFFERED: "ARENA_OPENING_MOVE_PAIR_NOT_OFFERED",
  INVALID_LEVERAGE: "ARENA_OPENING_MOVE_INVALID_LEVERAGE",
  MARGIN_ABOVE_STARTING_BALANCE: "ARENA_OPENING_MOVE_MARGIN_ABOVE_STARTING_BALANCE",
  BELOW_MIN_NOTIONAL: "ARENA_OPENING_MOVE_BELOW_MIN_NOTIONAL",
  INVALID_TRIGGER_PRICE: "ARENA_OPENING_MOVE_INVALID_TRIGGER_PRICE",
  ALREADY_PLACED: "ARENA_OPENING_MOVE_ALREADY_PLACED",
  NOT_SUPPORTED: "ARENA_OPENING_MOVE_NOT_SUPPORTED",
} as const;
export type ARENA_OPENING_MOVE_ERROR_CODE =
  (typeof ARENA_OPENING_MOVE_ERROR_CODE)[keyof typeof ARENA_OPENING_MOVE_ERROR_CODE];
